import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useScope } from "@/context/ScopeContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import ApaarExecutiveTab from "@/components/ApaarExecutiveTab";
import ApaarBlockPerformanceTab from "@/components/ApaarBlockPerformanceTab";
import ApaarSchoolActionTab from "@/components/ApaarSchoolActionTab";
import ApaarClassAnalysisTab from "@/components/ApaarClassAnalysisTab";
import ApaarInsightsTab from "@/components/ApaarInsightsTab";
import "@/components/ApaarExecutiveTab.css";
import "@/components/ApaarBlockPerformanceTab.css";
import "@/components/ApaarSchoolActionTab.css";
import "@/components/ApaarClassAnalysisTab.css";
import "@/components/ApaarInsightsTab.css";
import { Users, RefreshCw, Upload, School, BarChart3, Target, BookOpen, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import ExportPanel from "@/components/ExportPanel";
import DashboardPageHeader from "@/components/DashboardPageHeader";
import { getBackendUrl } from "@/lib/backend";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const TAB_ITEMS = [
  { id: "executive", label: "Executive Overview", icon: BarChart3 },
  { id: "blocks", label: "Block Performance", icon: Target },
  { id: "schools", label: "School Action", icon: School },
  { id: "class", label: "Class Analysis", icon: BookOpen },
  { id: "insights", label: "Insights", icon: Lightbulb },
];

const APAARDashboard = () => {
  const { scope } = useScope();
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [overview, setOverview] = useState(null);
  const [funnelData, setFunnelData] = useState([]);
  const [blockData, setBlockData] = useState([]);
  const [classData, setClassData] = useState([]);
  const [topPending, setTopPending] = useState([]);
  const [lowPerforming, setLowPerforming] = useState([]);
  const [riskData, setRiskData] = useState(null);
  const [activeTab, setActiveTab] = useState("executive");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Build scope parameters - the axios interceptor will merge them with localStorage values
      // Also pass district_name, block_name, school_name to help with matching
      const requestParams = {};
      if (scope.districtCode && scope.districtCode.trim()) {
        requestParams.district_code = scope.districtCode.trim();
      }
      if (scope.districtName && scope.districtName.trim()) {
        requestParams.district_name = scope.districtName.trim();
      }
      if (scope.blockCode && scope.blockCode.trim()) {
        requestParams.block_code = scope.blockCode.trim();
      }
      if (scope.blockName && scope.blockName.trim()) {
        requestParams.block_name = scope.blockName.trim();
      }
      if (scope.udiseCode && scope.udiseCode.trim()) {
        requestParams.udise_code = scope.udiseCode.trim();
      }
      if (scope.schoolName && scope.schoolName.trim()) {
        requestParams.school_name = scope.schoolName.trim();
      }

      console.log('Fetching APAAR data with scope:', {
        districtCode: scope.districtCode,
        blockCode: scope.blockCode,
        udiseCode: scope.udiseCode,
        params: requestParams,
        apiUrl: API
      });

      const [overviewRes, funnelRes, blockRes, classRes, pendingRes, lowRes, riskRes] = await Promise.all([
        axios.get(`${API}/apaar/overview`, { params: requestParams }),
        axios.get(`${API}/apaar/status-funnel`, { params: requestParams }),
        axios.get(`${API}/apaar/block-wise`, { params: requestParams }),
        axios.get(`${API}/apaar/class-wise`, { params: requestParams }),
        axios.get(`${API}/apaar/top-pending-schools`, { params: requestParams }),
        axios.get(`${API}/apaar/low-performing-schools`, { params: requestParams }),
        axios.get(`${API}/apaar/risk-schools`, { params: requestParams })
      ]);
      
      console.log('APAAR data fetched successfully:', {
        overviewTotalSchools: overviewRes?.data?.total_schools,
        overviewTotalStudents: overviewRes?.data?.total_students,
        blockDataCount: Array.isArray(blockRes?.data) ? blockRes.data.length : 0,
        hasFunnelData: Array.isArray(funnelRes?.data) && funnelRes.data.length > 0
      });
      
      setOverview(overviewRes.data);
      setFunnelData(Array.isArray(funnelRes.data) ? funnelRes.data : []);
      setBlockData(Array.isArray(blockRes.data) ? blockRes.data : []);
      setClassData(Array.isArray(classRes.data) ? classRes.data : []);
      setTopPending(Array.isArray(pendingRes.data) ? pendingRes.data : []);
      setLowPerforming(Array.isArray(lowRes.data) ? lowRes.data : []);
      setRiskData(riskRes.data || null);
    } catch (error) {
      console.error("Error fetching APAAR data:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        code: error.code
      });
      
      // Handle network errors (including ERR_BLOCKED_BY_CLIENT from browser extensions)
      if (error.code === 'ERR_NETWORK' || error.message?.includes('ERR_BLOCKED_BY_CLIENT') || error.message?.includes('Failed to fetch')) {
        console.warn("Network error detected - may be caused by browser extension");
        toast.error("Network error: Please disable ad blockers or privacy extensions and try again");
        setLoading(false);
        return;
      }
      
      const errorMsg = error.response?.data?.detail || error.message || "Failed to load APAAR data";
      
      // Only show error toast for actual errors, not for empty results
      if (error.response?.status && error.response?.status !== 404 && error.response?.status >= 500) {
        toast.error(errorMsg);
      }
      
      // Reset data on error to show "No Data" message (but not on network errors)
      if (error.response) {
        setOverview(null);
        setFunnelData([]);
        setBlockData([]);
        setClassData([]);
        setTopPending([]);
        setLowPerforming([]);
        setRiskData(null);
      }
    } finally {
      setLoading(false);
    }
  }, [scope.version, scope.districtCode, scope.blockCode, scope.udiseCode]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleImport = async () => {
    setImporting(true);
    try {
      await axios.post(`${API}/apaar/import`);
      toast.success("APAAR import started!");
      setTimeout(() => { fetchData(); setImporting(false); }, 10000);
    } catch (error) {
      toast.error("Import failed: " + (error.response?.data?.detail || error.message));
      setImporting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="loading-spinner" /></div>;

  // Check if we have data - overview should exist and have meaningful data
  const hasData = overview && typeof overview === 'object' && (
    (overview.total_schools > 0) || 
    (overview.total_students > 0) ||
    (Object.keys(overview).length > 3)
  );
  
  // Debug log to help diagnose data loading issues
  if (overview) {
    console.log('APAAR Dashboard data check:', {
      hasData,
      totalSchools: overview.total_schools,
      totalStudents: overview.total_students,
      overviewKeys: Object.keys(overview).length,
      overviewSample: Object.keys(overview).slice(0, 5)
    });
  }

  const scopeLabel =
    scope.schoolName?.trim() ||
    scope.blockName?.trim() ||
    scope.districtName?.trim() ||
    "Pune District";

  const headerCopy = {
    executive: {
      eyebrow: `Executive view · ${scopeLabel} · 2025–26`,
      subtitle:
        "School-wise APAAR generation monitoring with clear completion health, pending workload, consent gaps, failed records and priority school action queue.",
      icon: BarChart3,
    },
    blocks: {
      eyebrow: `Block performance · ${scopeLabel} · 2025–26`,
      subtitle: "Block-wise APAAR generation performance and priority ranking.",
      icon: Target,
    },
    schools: {
      eyebrow: `APAAR action view · ${scopeLabel} · 2025–26`,
      subtitle:
        "School-wise action workspace to identify high-pending institutions, assign field follow-up, and close consent or generation gaps before the next district review.",
      icon: School,
    },
    class: {
      eyebrow: `Class analysis · ${scopeLabel} · 2025–26`,
      subtitle:
        "Class-wise APAAR generation monitoring with stage diagnostics, early-grade risk detection and targeted closure actions for pending and not-applied students.",
      icon: BookOpen,
    },
    insights: {
      eyebrow: `Insights · ${scopeLabel} · 2025–26`,
      subtitle:
        "AI-assisted triage view with root-cause diagnostics, priority school watchlist, officer-owned actions and execution tracker.",
      icon: Lightbulb,
    },
  };

  const copy = headerCopy[activeTab] || headerCopy.executive;
  const EyebrowIcon = copy.icon;

  return (
    <div className="apaar-dashboard space-y-6 animate-fade-in" data-testid="apaar-dashboard">
      <DashboardPageHeader
        title="APAAR Entry Status Dashboard"
        subtitle={
          hasData
            ? copy.subtitle
            : "School-wise APAAR Generation • 2025-26 • Pune District"
        }
        eyebrow={
          hasData ? (
            <>
              <EyebrowIcon size={14} />
              {copy.eyebrow}
            </>
          ) : null
        }
        actions={
          <>
            <Button variant="outline" size="sm" onClick={handleImport} disabled={importing} data-testid="import-apaar-btn">
              {importing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {importing ? "Importing..." : "Import Data"}
            </Button>
            <ExportPanel dashboardName="apaar" dashboardTitle="APAAR Status" />
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </>
        }
      />

      {!hasData ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No APAAR Data Available</h3>
            <p className="text-slate-500 mb-4">Click "Import Data" to load the APAAR Entry Status Excel file</p>
            <Button onClick={handleImport} disabled={importing} data-testid="import-apaar-empty-btn">{importing ? "Importing..." : "Import APAAR Data"}</Button>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="apx-tabs-wrap" aria-label="APAAR dashboard sections">
            <nav className="apx-tabs">
              {TAB_ITEMS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  className={`apx-tab ${activeTab === id ? "active" : ""}`}
                  onClick={() => setActiveTab(id)}
                  data-testid={`tab-${id}`}
                >
                  <Icon />
                  {label}
                </button>
              ))}
            </nav>
          </div>

          <TabsContent value="executive" className="mt-0">
            <ApaarExecutiveTab
              overview={overview}
              funnelData={funnelData}
              riskData={riskData || {}}
              onNavigateTab={setActiveTab}
            />
          </TabsContent>

          <TabsContent value="blocks" className="mt-0">
            <ApaarBlockPerformanceTab overview={overview} blockData={blockData} />
          </TabsContent>

          <TabsContent value="schools" className="mt-0">
            <ApaarSchoolActionTab
              overview={overview}
              topPending={topPending}
              lowPerforming={lowPerforming}
              blockData={blockData}
            />
          </TabsContent>

          <TabsContent value="class" className="mt-0">
            <ApaarClassAnalysisTab overview={overview} classData={classData} />
          </TabsContent>
          <TabsContent value="insights" className="mt-0">
            <ApaarInsightsTab
              overview={overview}
              riskData={riskData}
              lowPerforming={lowPerforming}
            />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default APAARDashboard;
